import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo, useEffect, useRef, useState } from "react";
import { Select, MenuItem, FormControl, Chip, Box, OutlinedInput, Checkbox, Divider, SvgIcon, TextField, InputAdornment } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
const SELECT_ALL_VALUE = "__SELECT_ALL__";
export default function MultiSelectInput({ label, value = [], onChange, onBlur, errors, touched, options = [], placeholder = "Select...", name, required = false, maxDisplay = 3, disabled = false, readOnlyChips = false, hideSelectAll = false, showSearch = false, }) {
    const [open, setOpen] = useState(false);
    const [showAll, setShowAll] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const selectRef = useRef(null);
    const filteredOptions = useMemo(() => {
        if (!searchTerm)
            return options;
        const term = searchTerm.toLowerCase();
        return options.filter((opt) => opt.label.toLowerCase().includes(term) || opt.description?.toLowerCase().includes(term));
    }, [options, searchTerm]);
    // Ensure value is always an array
    const currentValue = useMemo(() => {
        if (!value)
            return [];
        return Array.isArray(value) ? value : [];
    }, [value]);
    // Reset showAll when value changes and items are less than maxDisplay
    useEffect(() => {
        if (currentValue.length <= maxDisplay) {
            setShowAll(false);
        }
    }, [currentValue.length, maxDisplay]);
    // Handle click outside to reset showAll
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (showAll && selectRef.current) {
                const isClickInside = selectRef.current.contains(event.target);
                // Reset showAll if clicking outside
                if (!isClickInside) {
                    setShowAll(false);
                }
            }
        };
        if (showAll) {
            // Use mousedown to catch clicks before they might trigger other events
            document.addEventListener("mousedown", handleClickOutside);
            return () => {
                document.removeEventListener("mousedown", handleClickOutside);
            };
        }
    }, [showAll]);
    // Close dropdown when clicking outside (e.g. "+ Add Network" or elsewhere)
    useEffect(() => {
        if (!open)
            return;
        const handleClickOutside = (event) => {
            const target = event.target;
            const insideSelect = selectRef.current?.contains(target);
            const insideMenu = event.target?.closest?.(".MuiMenu-root");
            if (!insideSelect && !insideMenu) {
                setOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [open]);
    // Check if all options are selected
    const allSelected = filteredOptions.length > 0 && filteredOptions.every(opt => currentValue.includes(opt.value));
    const someSelected = filteredOptions.some(opt => currentValue.includes(opt.value)) && !allSelected;
    const handleDelete = (valueToDelete, event) => {
        if (event) {
            event.stopPropagation();
            event.preventDefault();
        }
        const newValue = currentValue.filter((item) => item !== valueToDelete);
        onChange({
            target: {
                name: name,
                value: newValue,
            },
        });
    };
    const handleMoreClick = (event) => {
        event.stopPropagation();
        event.preventDefault();
        setShowAll(true);
    };
    const handleBlurEvent = (event) => {
        // Reset showAll when field loses focus
        setTimeout(() => {
            setShowAll(false);
        }, 100);
        if (onBlur) {
            onBlur(event);
        }
    };
    const displayCount = currentValue.length;
    const remainingCount = displayCount > maxDisplay ? displayCount - maxDisplay : 0;
    // Determine how many items to display
    const itemsToDisplay = showAll
        ? currentValue.length
        : Math.min(maxDisplay, currentValue.length);
    const handleChange = (event) => {
        const { target: { value: selectedValue }, } = event;
        // Filter out the SELECT_ALL special value
        let filteredValue = typeof selectedValue === "string"
            ? selectedValue.split(",").filter((v) => v && v !== SELECT_ALL_VALUE)
            : Array.isArray(selectedValue)
                ? selectedValue.filter((v) => v && v !== SELECT_ALL_VALUE)
                : [];
        // Check if SELECT_ALL was in the selection
        const hasSelectAll = typeof selectedValue === "string"
            ? selectedValue.includes(SELECT_ALL_VALUE)
            : Array.isArray(selectedValue) && selectedValue.includes(SELECT_ALL_VALUE);
        if (hasSelectAll) {
            // Toggle all selection
            if (allSelected) {
                const filteredValues = filteredOptions.map((opt) => opt.value);
                filteredValue = currentValue.filter((val) => !filteredValues.includes(val));
            }
            else {
                const filteredValues = filteredOptions.map((opt) => opt.value);
                filteredValue = Array.from(new Set([...currentValue, ...filteredValues]));
            }
        }
        onChange({
            target: {
                name: name,
                value: filteredValue,
            },
        });
    };
    const UncheckedIcon = (props) => (_jsx(SvgIcon, { ...props, viewBox: "0 0 24 24", style: { fill: "#D5D7DA" }, children: _jsx("rect", { x: "3", y: "3", width: "18", height: "18", rx: "4", fill: "none", stroke: "#D5D7DA", strokeWidth: "2" }) }));
    const CheckedIcon = (props) => (_jsxs(SvgIcon, { ...props, viewBox: "0 0 24 24", style: { fill: "#FF008E" }, children: [_jsx("rect", { x: "3", y: "3", width: "18", height: "18", rx: "4", fill: "transparent", stroke: "#FF008E" }), _jsx("path", { d: "M6 12l4 4 8-8", fill: "none", stroke: "#FF008E", strokeWidth: "2" })] }));
    const IndeterminateIcon = (props) => (_jsxs(SvgIcon, { ...props, viewBox: "0 0 24 24", style: { fill: "#FF008E" }, children: [_jsx("rect", { x: "3", y: "3", width: "18", height: "18", rx: "4", fill: "transparent", stroke: "#FF008E" }), _jsx("rect", { x: "6", y: "11", width: "12", height: "1", fill: "none", stroke: "#FF008E", strokeWidth: "1" })] }));
    const getAvatarBgColor = (index) => {
        const colors = [
            'multicolor-tag-one',
            'multicolor-tag-two',
            'multicolor-tag-three',
        ];
        return colors[index % colors.length];
    };
    return (_jsx(Box, { ref: selectRef, sx: { position: "relative", width: "100%" }, children: _jsxs(FormControl, { fullWidth: true, error: Boolean(errors && touched), className: "multicolor-tag-select", children: [label && (_jsxs("label", { className: "form-input-label", style: { marginBottom: '6px', fontSize: '14px', fontWeight: 500, color: '#344054', display: 'block' }, children: [label, required && _jsx("span", { style: { color: "#f04438", marginLeft: '2px' }, children: "*" })] })), _jsxs(Select, { name: name, multiple: true, value: currentValue, onChange: handleChange, onBlur: handleBlurEvent, onOpen: () => setOpen(true), onClose: () => {
                        setOpen(false);
                        // Reset showAll when dropdown closes
                        setShowAll(false);
                        setSearchTerm("");
                    }, open: open, displayEmpty: true, disabled: disabled, input: _jsx(OutlinedInput, { notched: false }), renderValue: () => (_jsxs(Box, { sx: {
                            display: "flex",
                            flexWrap: "wrap",
                            gap: 0.5,
                            minHeight: "24px",
                            maxHeight: showAll ? "120px" : "auto",
                            overflowY: showAll && currentValue.length > 6 ? "auto" : "visible",
                            overflowX: "hidden",
                            "&::-webkit-scrollbar": {
                                width: "6px",
                            },
                            "&::-webkit-scrollbar-track": {
                                backgroundColor: "#f5f5f5",
                                borderRadius: "3px",
                            },
                            "&::-webkit-scrollbar-thumb": {
                                backgroundColor: "#d5d7da",
                                borderRadius: "3px",
                                "&:hover": {
                                    backgroundColor: "#b5b7ba",
                                },
                            },
                        }, onMouseDown: (e) => {
                            // Only prevent if clicking on chips, not on empty space
                            if (e.target.closest(".MuiChip-root") ||
                                e.target.closest(".MuiChip-deleteIcon")) {
                                e.stopPropagation();
                            }
                        }, children: [currentValue.length === 0 && (_jsx("span", { style: { color: "#667085", fontSize: "14px", alignSelf: "center", paddingLeft: "4px" }, children: placeholder })), currentValue.slice(0, itemsToDisplay).map((val, userIndex) => {
                                const option = options.find((opt) => opt.value === val);
                                const optionLabel = option ? option.label : val;
                                const chipColor = option?.chipColor || "#f5f5f5";
                                const textColor = option?.textColor || "#414651";
                                return (_jsx(Chip, { className: `${getAvatarBgColor(userIndex)}`, label: optionLabel, onDelete: readOnlyChips || disabled
                                        ? undefined
                                        : (e) => {
                                            handleDelete(val, e);
                                        }, deleteIcon: readOnlyChips || disabled
                                        ? undefined
                                        : _jsx(CloseIcon, { style: { fontSize: "12px" } }), onClick: (e) => {
                                        e.stopPropagation();
                                    }, onMouseDown: (e) => {
                                        // Prevent select from opening when clicking on chip
                                        e.stopPropagation();
                                    }, sx: {
                                        backgroundColor: chipColor,
                                        color: textColor,
                                        fontSize: "14px",
                                        fontWeight: 500,
                                        height: "24px",
                                        borderRadius: "16px",
                                        cursor: "default",
                                        flexShrink: 0,
                                        textTransform: "capitalize",
                                        "& .MuiChip-deleteIcon": {
                                            color: textColor,
                                            fontSize: "12px",
                                            cursor: "pointer",
                                            "&:hover": {
                                                color: textColor,
                                            },
                                        },
                                        "&:hover": {
                                            backgroundColor: chipColor,
                                        },
                                    } }, val));
                            }), !showAll && remainingCount > 0 && (_jsx(Chip, { className: "multicolor-more", label: `+${remainingCount}`, onClick: handleMoreClick, sx: {
                                    backgroundColor: "#f5f5f5",
                                    color: "#414651",
                                    fontSize: "14px",
                                    fontWeight: 500,
                                    height: "24px",
                                    borderRadius: "16px",
                                    cursor: "pointer",
                                    flexShrink: 0,
                                    "&:hover": {
                                        backgroundColor: "#e5e5e5",
                                    },
                                } }))] })), className: "form-input-root", sx: {
                        "& .MuiSelect-select": {
                            padding: "10px 14px",
                            minHeight: "24px",
                        },
                    }, MenuProps: {
                        disablePortal: true,
                        PaperProps: {
                            sx: {
                                boxShadow: "0px 12px 16px -4px rgba(10, 13, 18, 0.08), 0px 4px 6px -2px rgba(10, 13, 18, 0.03)",
                                borderRadius: "8px",
                                border: "1px solid #f5f5f5",
                                mt: 1,
                            },
                        },
                    }, children: [showSearch && (_jsx(MenuItem, { disableRipple: true, sx: {
                                padding: "8px 16px",
                                cursor: "default",
                                "&:hover": { backgroundColor: "transparent" },
                            }, onKeyDown: (e) => e.stopPropagation(), children: _jsx(TextField, { fullWidth: true, variant: "outlined", size: "small", placeholder: "Search...", value: searchTerm, onChange: (e) => setSearchTerm(e.target.value), onClick: (e) => e.stopPropagation(), onKeyDown: (e) => e.stopPropagation(), InputProps: {
                                    startAdornment: (_jsx(InputAdornment, { position: "start", children: _jsx(SearchIcon, { fontSize: "small" }) })),
                                } }) })), showSearch && _jsx(Divider, { sx: { backgroundColor: "#f5f5f5", margin: 0 } }), !hideSelectAll && (_jsx(MenuItem, { value: SELECT_ALL_VALUE, className: "employee-portfolio-checkbox-list", sx: {
                                padding: "10px 16px",
                                cursor: "pointer",
                                "&:hover": { backgroundColor: "transparent" },
                            }, children: _jsxs(Box, { sx: { display: "flex", alignItems: "center", gap: "12px", width: "100%" }, children: [_jsx(Checkbox, { checked: allSelected, indeterminate: someSelected, icon: _jsx(UncheckedIcon, {}), checkedIcon: _jsx(CheckedIcon, {}), indeterminateIcon: _jsx(IndeterminateIcon, {}), sx: {
                                            padding: 0,
                                            color: "#d5d7da",
                                            "&.Mui-checked": { color: "#ff008e" },
                                            "&.MuiCheckbox-indeterminate": { color: "#ff008e" },
                                            "& .MuiSvgIcon-root": { fontSize: "16px" },
                                        } }), _jsxs("span", { style: { fontSize: "14px", fontWeight: 500, color: "#414651" }, children: [allSelected ? "Unselect All" : "Select All", " ", label] })] }) })), !hideSelectAll && _jsx(Divider, { sx: { backgroundColor: "#f5f5f5", margin: 0 } }), filteredOptions.length === 0 ? (_jsx(MenuItem, { disabled: true, sx: { padding: "10px 16px", color: "#667085", fontSize: "14px" }, children: "No options found" })) : (filteredOptions.map((option) => {
                            const isSelected = currentValue.includes(option.value);
                            return (_jsx(MenuItem, { value: option.value, sx: {
                                    padding: "10px 16px",
                                    "&:hover": {
                                        backgroundColor: "transparent",
                                    },
                                }, children: _jsxs(Box, { sx: { display: "flex", alignItems: "center", gap: "12px", width: "100%" }, children: [_jsx(Checkbox, { checked: isSelected, icon: _jsx(UncheckedIcon, {}), checkedIcon: _jsx(CheckedIcon, {}), indeterminateIcon: _jsx(IndeterminateIcon, {}), sx: {
                                                padding: 0,
                                                color: "#d5d7da",
                                                "&.Mui-checked": {
                                                    color: "#ff008e",
                                                },
                                                "& .MuiSvgIcon-root": {
                                                    fontSize: "16px",
                                                    width: "16px",
                                                    height: "16px",
                                                },
                                                "&:hover": {
                                                    backgroundColor: "transparent",
                                                },
                                            } }), _jsxs(Box, { sx: { display: "flex", flexDirection: "column", minWidth: 0 }, children: [_jsx(Box, { component: "span", sx: {
                                                        fontSize: "14px",
                                                        fontWeight: 500,
                                                        lineHeight: "20px",
                                                        color: "#414651",
                                                        textTransform: "capitalize",
                                                    }, children: option.label }), option.description && (_jsx(Box, { component: "span", sx: {
                                                        fontSize: "12px",
                                                        lineHeight: "18px",
                                                        color: "#667085",
                                                        overflow: "hidden",
                                                        textOverflow: "ellipsis",
                                                        whiteSpace: "nowrap",
                                                    }, children: option.description }))] })] }) }, option.value));
                        }))] }), errors && touched && (_jsx("div", { className: "error-label", children: _jsx("p", { children: errors }) }))] }) }));
}
